/**
 * Use Case Runs Chart
 * Horizontal bar chart showing run counts per use case from llm_usages
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { UseCaseRunCount, UseCaseCostItem, USECASE_LABELS } from '../types';

interface UseCaseRunsChartProps {
  data: UseCaseRunCount[];
  costData?: UseCaseCostItem[];
}

const BRAND_PRIMARY = '#03e8d3';

const CHART_COLORS = [
  '#03e8d3',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#F59E0B',
  '#EF4444',
  '#EC4899',
  '#14B8A6',
  '#6366F1',
  '#84CC16',
];

const formatCost = (value: number): string => {
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value >= 0.01) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(4)}`;
};

const normalizeKey = (s: string): string => s.toLowerCase().replace(/[-_\s]+/g, '');

const formatName = (name: string): string =>
  USECASE_LABELS[name] || name.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface DisplayEntry {
  displayName: string;
  count: number;
  totalCost: number | null;
  avgCostPerRun: number | null;
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: DisplayEntry }>;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const entry = payload[0].payload;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3"
      style={{ minWidth: 180 }}
    >
      <p className="text-sm font-semibold text-gray-900 mb-1">{entry.displayName}</p>
      <p className="text-sm text-gray-600">{entry.count.toLocaleString()} runs</p>
      {entry.totalCost !== null && (
        <>
          <p className="text-sm text-gray-600 mt-1">Total Cost: {formatCost(entry.totalCost)}</p>
          {entry.avgCostPerRun !== null && (
            <p className="text-sm text-gray-600">Avg Cost/Run: {formatCost(entry.avgCostPerRun)}</p>
          )}
        </>
      )}
    </div>
  );
};

export const UseCaseRunsChart: React.FC<UseCaseRunsChartProps> = ({ data, costData }) => {
  // Build cost lookup keyed by normalized parent_usecase
  // (run names come from simulations.use_case / llm_usages.usecase, cost names from llm_usages.usecase)
  const costByUseCase = React.useMemo(() => {
    if (!costData) return {} as Record<string, number>;
    const map: Record<string, number> = {};
    let healthcareTotalCost = 0;
    costData.forEach((item) => {
      const key = normalizeKey(item.parent_usecase);
      map[key] = (map[key] || 0) + item.total_cost;
      if (key.startsWith('healthcare')) {
        healthcareTotalCost += item.total_cost;
      }
    });
    if (healthcareTotalCost > 0) {
      map[normalizeKey('Healthcare Agentic Chat')] = healthcareTotalCost;
    }
    return map;
  }, [costData]);

  const displayData: DisplayEntry[] = React.useMemo(() => {
    return (data || []).map((item) => {
      const totalCost = costByUseCase[normalizeKey(item.name)] ?? null;
      const avgCostPerRun = totalCost !== null && item.count > 0 ? totalCost / item.count : null;
      return {
        displayName: formatName(item.name),
        count: item.count,
        totalCost,
        avgCostPerRun,
      };
    });
  }, [data, costByUseCase]);

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
          <h3 className="text-lg font-semibold text-gray-900">Use Case Runs</h3>
        </div>
        <div className="flex items-center justify-center h-48 text-gray-500">
          No run data available for this time period
        </div>
      </div>
    );
  }

  const totalRuns = displayData.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
          <h3 className="text-lg font-semibold text-gray-900">Use Case Runs</h3>
        </div>
        <span className="text-lg font-bold" style={{ color: BRAND_PRIMARY }}>
          {totalRuns.toLocaleString()} total
        </span>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(displayData.length * 44, 150)}>
        <BarChart
          data={displayData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 12, fill: '#6B7280' }} allowDecimals={false} />
          <YAxis
            dataKey="displayName"
            type="category"
            tick={{ fontSize: 12, fill: '#374151' }}
            width={180}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" name="Runs" radius={[0, 4, 4, 0]}>
            {displayData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default UseCaseRunsChart;
